import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import ChildProfile from "@/components/story/ChildProfile";
import SituationInput from "@/components/story/SituationInput";
import StorySettings from "@/components/story/StorySettings";
import Paywall from "@/components/story/Paywall";
import GenerationScreen from "@/components/story/GenerationScreen";
import ReadingScreen from "@/components/story/ReadingScreen";
import { loadState, saveState, defaultState, StoryState } from "@/lib/story-store";
import { trackEvent } from "@/lib/analytics";

const MakeGentleTale = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const devMode = searchParams.get("dev") === "true";

  const [state, setState] = useState<StoryState>(() => {
    const saved = loadState();
    // If no goal selected, redirect to archetypes
    if (!saved.goal) return defaultState;
    // Start at step 2 (ChildProfile) minimum
    return { ...saved, step: Math.max(saved.step, 2) };
  });

  useEffect(() => {
    if (!state.goal) {
      navigate("/archetypes", { replace: true });
    }
  }, []);

  useEffect(() => {
    saveState(state);
  }, [state]);

  const update = (partial: Partial<StoryState>) => {
    setState((prev) => {
      const next = { ...prev, ...partial };
      if (partial.step !== undefined && partial.step !== prev.step) {
        if (typeof window !== "undefined") window.scrollTo(0, 0);
      }
      return next;
    });
  };

  const reset = () => {
    if (typeof window !== "undefined") window.scrollTo(0, 0);
    saveState({ ...defaultState });
    navigate("/");
  };

  const { step } = state;

  return (
    <main className="min-h-screen bg-background">
      {step === 2 && (
        <ChildProfile state={state} onUpdate={update} onNext={() => update({ step: 3 })} onBack={() => navigate("/archetypes")} />
      )}
      {step === 3 && (
        <SituationInput
          value={state.situation}
          name={state.name}
          onChange={(situation) => update({ situation })}
          onNext={() => update({ step: 4 })}
          onBack={() => update({ step: 2 })}
        />
      )}
      {step === 4 && (
        <StorySettings
          state={state}
          onUpdate={update}
          onNext={() => {
            if (devMode) {
              update({ step: 6, paid: true });
            } else {
              trackEvent("view_paywall", devMode);
              update({ step: 5 });
            }
          }}
          onBack={() => update({ step: 3 })}
        />
      )}
      {step === 5 && (
        <Paywall
          name={state.name}
          gender={state.gender}
          onPay={() => {
            trackEvent("payment_complete", devMode);
            update({ step: 6, paid: true });
          }}
          onBack={() => update({ step: 4 })}
        />
      )}
      {step === 6 && (
        <GenerationScreen
          name={state.name}
          gender={state.gender}
          age={state.age}
          situation={state.situation}
          goal={state.goal}
          temperament={state.temperament}
          themes={state.themes}
          tone={state.tone}
          length={state.length}
          heroFormat={state.heroFormat}
          onComplete={(storyData) => update({ step: 7, storyData })}
        />
      )}
      {step === 7 && state.storyData && (
        <ReadingScreen storyData={state.storyData} name={state.name} goal={state.goal} situation={state.situation} onCreateAnother={reset} />
      )}
    </main>
  );
};

export default MakeGentleTale;
